import React, { useState, useEffect } from 'react';
import {
    Grid,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    InputAdornment,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Visibility as ViewIcon,
    ToggleOff as ToggleOffIcon,
    ToggleOn as ToggleOnIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import franchiseService from '../../services/franchiseService';
import authService from '../../services/authService';

const FranchiseManagement = () => {
    const [franchises, setFranchises] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editingFranchise, setEditingFranchise] = useState(null);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    });
    const { handleApiCallWithLoading, handleApiCall, showSuccess, showError } = useApiHandler();
    const [formData, setFormData] = useState({
        fname: '',
        lname: '',
        email: '',
        password: '',
        mobile: '',
        address: '',
        zipcode: '',
        state: '',
        city: '',
        country: '',
        web: '',
        status: '1'
    });

    useEffect(() => {
        console.log('FranchiseManagement component mounted');
        console.log('Current user:', authService.getCurrentUser());
        console.log('Token:', localStorage.getItem('token'));
        loadFranchises();
        loadCountries();
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (searchTerm !== '') {
            setPage(0);
            setPagination(prev => ({ ...prev, current_page: 1 }));
        }
        
        const timeoutId = setTimeout(() => {
            loadFranchises();
        }, searchTerm ? 300 : 0); // Debounce only for search

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const loadFranchises = async () => {
        try {
            setLoading(true);
            console.log('Loading franchises...');
            
            const params = {
                page: page + 1, // Convert 0-based to 1-based
                per_page: rowsPerPage
            };
            
            if (searchTerm) params.search = searchTerm;

            const response = await franchiseService.getFranchises(params);
            console.log('Franchise response:', response);
            if (response.success) {
                setFranchises(response.data);
                if (response.pagination) {
                    setPagination(response.pagination);
                }
                console.log('Franchises loaded:', response.data);
            } else {
                console.error('Failed to load franchises:', response);
                showError('Failed to load franchises');
            }
        } catch (error) {
            console.error('Error loading franchises:', error);
            showError('Failed to load franchises: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const loadCountries = async () => {
        try {
            console.log('Loading countries...');
            const response = await franchiseService.getCountries();
            console.log('Countries response:', response);
            if (response.success) {
                setCountries(response.data);
                console.log('Countries loaded:', response.data);
            } else {
                console.error('Failed to load countries:', response);
            }
        } catch (error) {
            console.error('Failed to load countries:', error);
        }
    };

    const loadStates = async (countryId) => {
        try {
            const response = await franchiseService.getStates(countryId);
            if (response.success) {
                setStates(response.data);
            }
        } catch (error) {
            console.error('Failed to load states:', error);
        }
    };

    const loadCities = async (stateId) => {
        try {
            const response = await franchiseService.getCities(stateId);
            if (response.success) {
                setCities(response.data);
            }
        } catch (error) {
            console.error('Failed to load cities:', error);
        }
    };

    const handleOpen = (franchise = null) => {
        if (franchise) {
            setEditingFranchise(franchise);
            setFormData({
                fname: franchise.fname,
                lname: franchise.lname,
                email: franchise.email,
                password: '',
                mobile: franchise.mobile,
                address: franchise.address,
                zipcode: franchise.zipcode,
                state: franchise.state,
                city: franchise.city,
                country: franchise.country,
                web: franchise.web || '',
                status: franchise.status
            });
            if (franchise.country) {
                loadStates(franchise.country);
            }
            if (franchise.state) {
                loadCities(franchise.state);
            }
        } else {
            setEditingFranchise(null);
            setFormData({
                fname: '',
                lname: '',
                email: '',
                password: '',
                mobile: '',
                address: '',
                zipcode: '',
                state: '',
                city: '',
                country: '',
                web: '',
                status: '1'
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditingFranchise(null);
        setStates([]);
        setCities([]);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Load dependent data
        if (name === 'country') {
            loadStates(value);
            setFormData(prev => ({ ...prev, state: '', city: '' }));
        } else if (name === 'state') {
            loadCities(value);
            setFormData(prev => ({ ...prev, city: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingFranchise) {
                const response = await franchiseService.updateFranchise(editingFranchise.id, formData);
                if (response.success) {
                    showSuccess('Franchise updated successfully');
                    loadFranchises();
                    handleClose();
                }
            } else {
                const response = await franchiseService.createFranchise(formData);
                if (response.success) {
                    showSuccess('Franchise created successfully');
                    loadFranchises();
                    handleClose();
                }
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this franchise?')) {
            try {
                const response = await franchiseService.deleteFranchise(id);
                if (response.success) {
                    showSuccess('Franchise deleted successfully');
                    loadFranchises();
                }
            } catch (error) {
                showError(error.response?.data?.message || 'Delete failed');
            }
        }
    };

    const handleToggleStatus = async (id) => {
        try {
            const response = await franchiseService.toggleFranchiseStatus(id);
            if (response.success) {
                showSuccess('Franchise status updated successfully');
                loadFranchises();
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Status update failed');
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
    };

    if (loading) {
        return (
            <DashboardLayout title="Franchise Management">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="Franchise Management">
            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h5">Franchises</Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpen()}
                    >
                        Add Franchise
                    </Button>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        placeholder="Search franchises by name, email, or location..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Mobile</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {franchises.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography variant="body2" color="textSecondary">
                                            No franchises found. {loading ? 'Loading...' : 'Click "Add Franchise" to create one.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                franchises.map((franchise) => (
                                    <TableRow key={franchise.id}>
                                        <TableCell>{franchise.full_name}</TableCell>
                                        <TableCell>{franchise.email}</TableCell>
                                        <TableCell>{franchise.mobile}</TableCell>
                                        <TableCell>
                                            {franchise.city_name || franchise.city}, {franchise.state_name || franchise.state}
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={franchise.is_active ? 'Active' : 'Inactive'}
                                                color={franchise.is_active ? 'success' : 'error'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton 
                                                onClick={() => handleToggleStatus(franchise.id)} 
                                                color="primary"
                                                title="Toggle Status"
                                            >
                                                {franchise.is_active ? <ToggleOnIcon /> : <ToggleOffIcon />}
                                            </IconButton>
                                            <IconButton 
                                                onClick={() => handleOpen(franchise)} 
                                                color="primary"
                                                title="Edit"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton 
                                                onClick={() => handleDelete(franchise.id)} 
                                                color="error"
                                                title="Delete"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={pagination.total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelDisplayedRows={({ from, to, count }) => 
                        `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                    }
                />
            </Paper>

            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <form onSubmit={handleSubmit}>
                    <DialogTitle>
                        {editingFranchise ? 'Edit Franchise' : 'Add New Franchise'}
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    name="fname"
                                    value={formData.fname}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    name="lname"
                                    value={formData.lname}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={editingFranchise ? "Password (leave blank to keep current)" : "Password"}
                                    name="password"
                                    type="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required={!editingFranchise}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Mobile"
                                    name="mobile"
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Zipcode"
                                    name="zipcode"
                                    value={formData.zipcode}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Address"
                                    name="address"
                                    multiline
                                    rows={3}
                                    value={formData.address}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth required>
                                    <InputLabel>Country</InputLabel>
                                    <Select
                                        name="country"
                                        value={formData.country}
                                        onChange={handleChange}
                                        label="Country"
                                    >
                                        {countries.map((country) => (
                                            <MenuItem key={country.country_id} value={country.country_id}>
                                                {country.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth required>
                                    <InputLabel>State</InputLabel>
                                    <Select
                                        name="state"
                                        value={formData.state}
                                        onChange={handleChange}
                                        label="State"
                                        disabled={!formData.country}
                                    >
                                        {states.map((state) => (
                                            <MenuItem key={state.state_id} value={state.state_id}>
                                                {state.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth required>
                                    <InputLabel>City</InputLabel>
                                    <Select
                                        name="city"
                                        value={formData.city}
                                        onChange={handleChange}
                                        label="City"
                                        disabled={!formData.state}
                                    >
                                        {cities.map((city) => (
                                            <MenuItem key={city.city_id} value={city.city_id}>
                                                {city.city_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Website"
                                    name="web"
                                    value={formData.web}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        label="Status"
                                    >
                                        <MenuItem value="1">Active</MenuItem>
                                        <MenuItem value="0">Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button type="submit" variant="contained">
                            {editingFranchise ? 'Update' : 'Create'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </DashboardLayout>
    );
};

export default FranchiseManagement; 